import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, BrewersUnitOfWork } from 'app/core/core.module';
import { InvoiceService } from 'app/core/invoice.service';
import { Invoice, InvoiceStatus, Payment } from 'app/core/model/entity-model';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';

@Component({
    standalone: false,
    selector: 'app-payment-overview',
    templateUrl: './payment-overview.component.html',
    styleUrls: ['./payment-overview.component.scss'],
})
export class PaymentOverviewComponent implements OnInit {
    payments: Payment[];
    invoices: Invoice[];
    invoiceStatuses: InvoiceStatus[];
    rentals: any[];
    currentUserData: any;
    processing = false;
    isPaymentAdmin = false;

    @Input() showAsComponent: boolean = false;
    @Input() customerId: number | null = null;
    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    @ViewChild('openInvoicesDataGrid') openInvoicesDataGrid: DxDataGridComponent;

    constructor(
        private uow: BrewersUnitOfWork,
        private router: Router,
        private appInfo: AppInformationService,
        private auth: AuthService,
        private invoiceService: InvoiceService
    ) {}

    async ngOnInit() {
        this.invoiceStatuses = this.uow.lookups.invoiceStatuses as InvoiceStatus[];
        const paymentMethods = await this.uow.invoicePaymentMethods.whereActive();
        this.isPaymentAdmin = this.auth.hasRole('Payments:Admin') || true;
        await this.getData();
    }

    async getData() {
        this.invoices = [];
        this.payments = [];
        this.invoices = await this.uow.invoices.getOpenInvoices('customer, paymentLines');
        this.payments = await this.uow.invoices.getPayments('customer, paymentLines.invoice');
    }

    onRowDoubleClick(e) {
        if (!this.isPaymentAdmin) return;
        this.receivePayment();
    }

    onPaymentDoubleClick(e) {
        if (!this.isPaymentAdmin) return;
        this.openPayment(e.data.id);
    }

    openPayment(id: number) {
        if (!this.isPaymentAdmin) return;
        if (!id) return;

        this.router.navigate([`/payments/${id}`]);
    }

    open(invoice?: Invoice) {
        if (!this.isPaymentAdmin) return;
        this.router.navigate([`/invoices/${invoice ? invoice.id : 'new'}`]);
    }

    async delete(selectedRow: Payment) {
        const result = await confirm('Are you sure you want to delete the selected PAYMENT?', this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        const payment = (await this.uow.payments.byId(selectedRow.id, 'paymentLines.invoice')) as Payment;
        payment.paymentLines.forEach((paymentLine) => {
            paymentLine.invoice.paid = null;
            paymentLine.invoice.statusId = InvoiceStatus.Statuses.Posted;
            paymentLine.deleted = new Date();
            paymentLine.deletedBy = this.auth.currentUser.userName;
        });
        payment.deleted = new Date();
        payment.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit();
        const invoiceIds = payment.paymentLines.map((paymentLine) => paymentLine.invoiceId);
        await this.invoiceService.updateBalances(invoiceIds);
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async receivePayment() {
        const invoice = this.openInvoicesDataGrid.instance.getSelectedRowsData()[0];
        if (!invoice) return;

        const fullPaymentReceived = await this.invoiceService.receivePayment(invoice);

        await this.uow.commit();
        if (!fullPaymentReceived) {
            this.router.navigate([`/payments/new/${invoice.customerId}/${invoice.id}`]);
        } else {
            this.appInfo.pleaseWaitShow();
            await this.getData();
            this.appInfo.pleaseWaitHide();
        }
    }

    async applyCredit() {
        const invoice = this.openInvoicesDataGrid.instance.getSelectedRowsData()[0];
        if (!invoice) return;

        const result = await confirm('Are you sure you want to apply a Credit to the selected invoice?', this.appInfo.appTitle);
        if (!result) return;

        this.appInfo.pleaseWaitShow();

        const response = await this.invoiceService.createCreditInvoice(invoice.id);
        if (!response.success) {
            await alert(response.message, 'Invoice Not Created');
            return;
        }
        this.appInfo.pleaseWaitHide();
        await this.router.navigate([`/invoices/${response.value}`]);
    }

    async payWithoutQuickBooks(payment: Payment) {
        if (!payment || payment.quickBooksId || payment.quickBooksLastExport) return;

        const success = await confirm('Are you sure you want to mark this payment as paid without QuickBooks?', this.appInfo.appTitle);
        if (!success) return;

        this.appInfo.pleaseWaitShow();
        payment.quickBooksLastExport = new Date();
        payment.quickBooksId = 'Paid w/o QB';
        await this.uow.commit([payment]);
        this.appInfo.pleaseWaitHide();
    }
}
